import classNames from 'classnames'
import * as React from 'react'
import QuestionCircle from 'static/images/tooltip/question-circle.svg'

import { TestableInterface } from '../interfaces/TestableInterface'
import { makeTranslatable, TranslatableType } from '../interfaces/Translatable'
import { Tooltip } from '../Tooltip/Tooltip'
import './ToggleInput.scss'

export interface ToggleInputProps extends TestableInterface {
  value: string
  name?: string
  radio?: boolean
  map: { [key: string]: string | JSX.Element }
  label?: TranslatableType
  onChange?: (value: string) => void
  onBlur?: (value: string) => void
  inactiveItems?: string[] | []
  labelHelpText?: TranslatableType
  invalidItems?: string[]
}

export class ToggleInput extends React.Component<ToggleInputProps, null> {
  private isSingleToggle() {
    return Object.keys(this.props.map).length === 1
  }

  private renderLabel(): JSX.Element {
    if (this.props.label) {
      return (
        <span data-automation={this.props.automation + '-label'} className={'Label'}>
          {makeTranslatable(this.props.label)}
          {this.props.labelHelpText && (
            <Tooltip
              placement={'auto'}
              header={{ body: this.props.labelHelpText }}
              bodyJSX={<img className={`ReactTooltip__help-image`} src={QuestionCircle} alt={''} />}
            />
          )}
        </span>
      )
    }
  }

  private change(value: string) {
    if (this.isSingleToggle() && value === this.props.value) {
      value = undefined
    }
    this.props.onChange(value)
    if (this.props.onBlur) {
      this.props.onBlur(value)
    }
  }

  private renderToggles() {
    const result = []
    const self = this
    const width = 100 / Object.keys(this.props.map).length + '%'
    const value = this.props.value
    Object.keys(this.props.map).forEach((key) => {
      const text = self.props.map[key]
      const classes = ['toggle']

      if (value === key) {
        classes.push('selected')
      }

      result.push(
        <div
          key={key}
          className={classes.join(' ')}
          style={{ width }}
          data-automation={self.props.automation + '-' + key}
          onClick={() => {
            self.change(key)
          }}
        >
          <span>{text}</span>
        </div>,
      )
    })
    return result
  }

  private renderRadios() {
    const result = []
    const self = this
    const value = this.props.value
    const { map, invalidItems } = this.props
    Object.keys(this.props.map).forEach((key) => {
      const id = `radio-${self.props.name}-${key}`
      const invalid = invalidItems?.includes(key) ? 'invalid' : ''

      if (typeof self.props.map[key] === 'string') {
        const text = self.props.map[key]
        result.push(
          <div
            key={key}
            className={classNames(
              'radio',
              {
                inactive: self.props.inactiveItems && self.props.inactiveItems.includes(key),
              },
              invalid,
            )}
            data-automation={self.props.automation + '-' + key}
            onClick={self.change.bind(self, key)}
          >
            <div className={`checkable ${value === key ? 'checked' : ''}`} id={id} />
            <label className={invalid}> {text}</label>
          </div>,
        )
      } else {
        result.push(
          <div
            key={key}
            className={`radio ${invalid}`}
            data-automation={self.props.automation + '-' + key}
            onClick={self.change.bind(self, key)}
          >
            <div className={`checkable ${value === key ? 'checked' : ''}`} id={id} />
            {self.props.map[key]}
          </div>,
        )
      }
    })
    return result
  }

  public render(): JSX.Element {
    const inputType = this.props.radio ? 'Radio' : 'Toggle'
    return (
      <div className={`Controlled ${inputType}Input Input`} value={this.props.value}>
        {this.renderLabel()}
        <div
          data-automation={this.props.automation}
          data-value={this.props.value}
          className={`Controlled ${inputType}s`}
        >
          {this.props.radio ? this.renderRadios() : this.renderToggles()}
        </div>
      </div>
    )
  }
}
